import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import GalleryPhoto from "components/Photos/PhotoTool/Gallery/GalleryPhoto.tsx";
import { getPhotoSizeSetting, photoSizeMetaData } from "components/Photos/PhotoTool/Shared";
import { PhotoProperties } from "interfaces/Mueller.Business.Models.ts";
import WebConfig from "middlewares/WebConfig";
import { PhotoPropertiesPhotoSvc } from "typeConstants/photos/photos";
import ReorderableGrid, { ReorderableItem } from "components/Shared/ReorderableGrid";

interface MGalleryProps { 
  photos: PhotoPropertiesPhotoSvc[],
  scrollableView: boolean,
  onElementClick: ImageComponentClickHandler,
  multiSelect: boolean,
  selectedPhotoIds: string[], // Ids of the currently selected photos, used for multiselect
  sortingPhotos: boolean,
  onSortEndCallback: (nextIds: string[]) => void,
  isDisplayingDeletedPhotos?: boolean,
  editedPhotoIds: string[],
  customerSendEmailView?: boolean, // for when the photo tool is being loaded into the "send email" view in case actions that customers use
  selectedPhotoTags: string[],
  addSelectedTag: (tag: string) => void,
  //Redux State
  preferences: any,
  ssoToken: string,
}

type ImageComponentClickHandler = (
  event: React.MouseEvent,
  photo: Object & {
    index: number
  },
) => void

export interface GalleryPhotoPhotoProps {
    src: string,
    width: number,
    height: number,
    alt: string,
    index: number,
    photoid: string, // photoid is all lower case here to suppress dom element warning
    key: string,
}

export interface ExtendedPhotoProperties extends PhotoProperties {
  index: number,
}

export interface ExtendedPhotoSvcProperties extends PhotoPropertiesPhotoSvc {
  index: number,
}

const MGallery = (props: MGalleryProps) => {

  const photoDimensionsSetter = () => {
    return props.customerSendEmailView ? photoSizeMetaData.SMALL.dimensions : getPhotoSizeSetting(props.preferences).dimensions;
  };

  const [photoDimensions, setPhotoDimensions] = useState<{ width: number; height: number } | null>(photoDimensionsSetter);

  useEffect(() => {
    setPhotoDimensions(photoDimensionsSetter());
  }, [props.preferences]);

  const getFilteredPhotos = (): ExtendedPhotoSvcProperties[] => {
    if (!props.photos) 
      return [];
    if (!props.selectedPhotoTags?.length)
      return props.photos.map((photo: PhotoPropertiesPhotoSvc, index: number) => ({ ...photo, index }));
    return props.photos
      .map((photo : PhotoPropertiesPhotoSvc, index: number) => ({ ...photo, index }))
      .filter((photo) =>
        photo.Tags
          .map(photoTag => photoTag.trim())
          .some(photoTag => props.selectedPhotoTags.includes(photoTag))
      );
  };

  const calcWidthAndHeight = (photo: ExtendedPhotoSvcProperties) => {
    const photoIsPortrait = photo.ImageDimensions.height > photo.ImageDimensions.width;

    // If it's landscape we just use the natural dimensions
    if (!photoIsPortrait) {
      return { width: photoDimensions?.width, height: photoDimensions?.height };
    }

    // If it's portrait, but it's smaller than the user's selected image size dimensions, return photo's dimensions
    if (photo.ImageDimensions.height < photoDimensions?.height && photo.ImageDimensions.width < photoDimensions?.width) {
      return { width: photo.ImageDimensions.width, height: photo.ImageDimensions.height };
    }

    // If it's portrait and larger than the user's selected image size dimensions, we need to scale it down to fit.
    // The height will be the max height of the user's preferences, then the width is scaled to maintain aspect ratio.
    const aspectRatio = photo.ImageDimensions.width / photo.ImageDimensions.height;
    const calculatedWidth = Math.round(photoDimensions.height * aspectRatio);
    return { width: calculatedWidth, height: photoDimensions?.height };
  };

  const getGalleryPhotos = (): GalleryPhotoPhotoProps[] => {
    return getFilteredPhotos().map((photo: ExtendedPhotoSvcProperties) => {
      const { width, height } = calcWidthAndHeight(photo);
      return {
        src: `${WebConfig.photoService}/${photo.BucketUri}/${photo.Id}?SSO=${props.ssoToken}&width=${width}&height=${height}${props.editedPhotoIds?.includes(photo.Id) ? `&t=${new Date().valueOf().toString()}` : ''}`,
        width,
        height,
        alt: photo.Tags.join(),
        key: photo.Id,
        index: photo.index,
        photoid: photo.Id, // photoid is all lower case here to suppress dom element warning
      };
    });
  };

  const getSortablePhotos = (): ReorderableItem[] => {
    return getFilteredPhotos().map((photo: ExtendedPhotoSvcProperties) => {
      const { width, height } = calcWidthAndHeight(photo);
      return {
        id: photo.Id,
        node: (
          <img
            src={`${WebConfig.photoService}/${photo.BucketUri}/${photo.Id}?SSO=${props.ssoToken}&width=${width}&height=${height}${props.editedPhotoIds?.includes(photo.Id) ? `&t=${new Date().valueOf().toString()}` : ''}`}
            width={width}
            height={height}
            key={photo.Id}
          />
        )
      };
    });
  };

  const isSelected = (id: string) => {
    return props.selectedPhotoIds && props.selectedPhotoIds.includes(id);
  };

  const onSortEnd = (nextIds: string[]) => {
    const reOrderedPhotoArray = [];
    nextIds.forEach((id) => {
      const photo = props.photos.find((p) => p.Id === id);
      if (photo) {
        reOrderedPhotoArray.push(photo);
      }
    });
    props.onSortEndCallback(reOrderedPhotoArray);
  };

  const onElementClick = props.onElementClick;
  const multiSelect = props.multiSelect;

  return (
    props.sortingPhotos ?
    <ReorderableGrid
      items={getSortablePhotos()}
      onReorder={onSortEnd}
      layout="grid"
      itemClassName="reorderable-photo"
      className="reorderable-gallery"
    />
    :
      <div className="photo-gallery" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${photoDimensions?.width}px, 1fr))` }}> {/* The grid styling is dependent on the photo dimensions */}
          {getGalleryPhotos().map((photo) => (
            <GalleryPhoto
              key={photo.key}
              photo={photo}
              onClick={onElementClick}
              isSelected={isSelected}
              multiSelect={multiSelect}
              index={photo.index}
              customerSendEmailView={props.customerSendEmailView}
              addSelectedPhotoTag={props.addSelectedTag}
            />
          ))}
        </div>
  );
};

const mapStateToProps = state => ({
  preferences: state.preferences,
  ssoToken: state.user.profile.token,
});

export default connect(mapStateToProps, null)(MGallery);
